import React, { useMemo, useState } from 'react';
import { Check, X, Loader2, AlertTriangle, Mail, Table, CalendarClock, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';

const KINDS = {
  email: ['Send an email', Mail],
  sheet_rows: ['Add rows to a sheet', Table],
  interview: ['Schedule an interview', CalendarClock],
  page: ['Create an Officium page', FileText],
};

const list = value => (Array.isArray(value) ? value.join(', ') : value || '');
const split = value => value.split(/[,;\n]/).map(item => item.trim()).filter(Boolean);

const Field = ({ label, children }) => (
  <label className="block space-y-1">
    <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">{label}</span>
    {children}
  </label>
);

/** Nothing happens until the user reviews these exact details and presses Confirm. */
export const AiActionPreview = ({ action, sheets = [], busy, onConfirm, onDiscard }) => {
  const [form, setForm] = useState(() => action.payload || {});
  const [label, Icon] = KINDS[action.kind] || ['Proposed action', AlertTriangle];
  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const sheet = useMemo(() => sheets.find(item => item.id === form.sheet_id), [sheets, form.sheet_id]);
  const rows = form.rows || [];

  const confirm = () => {
    const payload = { ...form };
    if (action.kind === 'email') payload.to = split(list(form.to));
    if (action.kind === 'interview') payload.attendee_emails = split(list(form.attendee_emails));
    if (action.kind === 'page') payload.tags = split(list(form.tags));
    onConfirm(payload);
  };

  return (
    <div className="rounded-xl border border-indigo-200 bg-indigo-50/70 p-3 text-xs" data-testid="kb-ai-action-preview">
      <div className="mb-2 flex items-center gap-1.5 font-medium text-indigo-800">
        <Icon className="h-3.5 w-3.5" />{label}
      </div>
      {action.summary && <p className="mb-2 text-[11px] text-slate-600" data-testid="kb-ai-action-summary">{action.summary}</p>}
      {(action.missing || []).length > 0 && (
        <p className="mb-2 flex items-start gap-1 rounded-lg bg-amber-100 px-2 py-1.5 text-[11px] text-amber-900" data-testid="kb-ai-action-missing">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />Fill in: {action.missing.join(', ')}
        </p>
      )}

      <div className="space-y-2">
        {action.kind === 'email' && (<>
          <Field label="To"><Input className="h-8 text-xs" value={list(form.to)} onChange={e => set('to', e.target.value)} data-testid="kb-ai-action-to" /></Field>
          <Field label="Subject"><Input className="h-8 text-xs" value={form.subject || ''} onChange={e => set('subject', e.target.value)} data-testid="kb-ai-action-subject" /></Field>
          <Field label="Message"><Textarea rows={6} className="text-xs" value={form.body_text || ''} onChange={e => set('body_text', e.target.value)} data-testid="kb-ai-action-body" /></Field>
        </>)}

        {action.kind === 'sheet_rows' && (<>
          <Field label="Sheet">
            <select className="h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-xs" value={form.sheet_id || ''}
              onChange={e => set('sheet_id', e.target.value)} data-testid="kb-ai-action-sheet">
              <option value="">Choose a sheet</option>
              {sheets.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </Field>
          <p className="text-[11px] text-slate-500" data-testid="kb-ai-action-rows-count">{rows.length} row(s) will be added{sheet ? ` to ${sheet.name}` : ''}.</p>
          <div className="max-h-40 overflow-auto rounded-lg bg-white p-2">
            {rows.slice(0, 8).map((row, index) => (
              <div key={index} className="border-b border-slate-100 py-1 text-[11px] text-slate-600 last:border-0 [overflow-wrap:anywhere]">
                {Object.entries(row).map(([field, value]) => `${field}: ${value}`).join(' · ')}
              </div>
            ))}
          </div>
        </>)}

        {action.kind === 'interview' && (<>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Candidate"><Input className="h-8 text-xs" value={form.candidate_name || ''} onChange={e => set('candidate_name', e.target.value)} data-testid="kb-ai-action-candidate" /></Field>
            <Field label="Email"><Input className="h-8 text-xs" value={form.candidate_email || ''} onChange={e => set('candidate_email', e.target.value)} data-testid="kb-ai-action-candidate-email" /></Field>
            <Field label="Date"><Input type="date" className="h-8 text-xs" value={form.scheduled_date || ''} onChange={e => set('scheduled_date', e.target.value)} data-testid="kb-ai-action-date" /></Field>
            <Field label="Time"><Input type="time" className="h-8 text-xs" value={form.scheduled_time || ''} onChange={e => set('scheduled_time', e.target.value)} data-testid="kb-ai-action-time" /></Field>
            <Field label="Type">
              <select className="h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-xs" value={form.interview_type || 'phone'}
                onChange={e => set('interview_type', e.target.value)} data-testid="kb-ai-action-type">
                {['phone', 'video', 'in-person', 'meeting', 'discussion'].map(value => <option key={value} value={value}>{value}</option>)}
              </select>
            </Field>
            <Field label="Minutes"><Input type="number" className="h-8 text-xs" value={form.duration_minutes || 30} onChange={e => set('duration_minutes', Number(e.target.value))} data-testid="kb-ai-action-duration" /></Field>
          </div>
          <Field label="Other attendees"><Input className="h-8 text-xs" value={list(form.attendee_emails)} onChange={e => set('attendee_emails', e.target.value)} data-testid="kb-ai-action-attendees" /></Field>
          <Field label="Notes"><Textarea rows={3} className="text-xs" value={form.notes || ''} onChange={e => set('notes', e.target.value)} data-testid="kb-ai-action-notes" /></Field>
        </>)}

        {action.kind === 'page' && (<>
          <Field label="Title"><Input className="h-8 text-xs" value={form.title || ''} onChange={e => set('title', e.target.value)} data-testid="kb-ai-action-title" /></Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Department"><Input className="h-8 text-xs" value={form.department || ''} onChange={e => set('department', e.target.value)} data-testid="kb-ai-action-department" /></Field>
            <Field label="Tags"><Input className="h-8 text-xs" value={list(form.tags)} onChange={e => set('tags', e.target.value)} data-testid="kb-ai-action-tags" /></Field>
          </div>
          <Field label="Content (saved as a draft)"><Textarea rows={6} className="text-xs" value={form.html_body || ''} onChange={e => set('html_body', e.target.value)} data-testid="kb-ai-action-html" /></Field>
        </>)}
      </div>

      <div className="mt-3 flex items-center gap-2">
        <Button size="sm" className="h-7 bg-indigo-600 px-3 text-xs hover:bg-indigo-700" onClick={confirm} disabled={busy} data-testid="kb-ai-action-confirm">
          {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Check className="mr-1 h-3 w-3" />}Confirm
        </Button>
        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-slate-500" onClick={onDiscard} disabled={busy} data-testid="kb-ai-action-discard">
          <X className="mr-1 h-3 w-3" />Discard
        </Button>
      </div>
    </div>
  );
};
